// src/oura/components/newscan/WordsView.tsx
//
// The Words view of the Review step: the words that write the 50, grouped by
// what they are (the problem, what they sell, who it is for, ...). Change one
// and every question using it changes, except locked questions, which keep
// their wording. Each save says what it did under the word ("Changed in 3
// questions. 1 locked question kept its wording.").
//
// "Draft the words again" at the foot of the list picks fresh words (two AI
// calls, so cents) and redrafts the 50 from them.
//
// The Review step renders this inside its Words tab panel. To bring one word
// into view (an "open word" decision, a "Built from" link in the question
// editor), pass its variable as `focusVariable`; the view opens its group,
// scrolls to it, focuses the field and calls `onFocusDone`.

import { RefreshCwIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ScanDraftEffect, ScanDraftQuestion, ScanDraftWord, WordFamily } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { FoldScope, GroupFold, PillButton, Surface, TYPE, useFoldOpener, useMediaQuery, REDUCED_MOTION } from "@oura/ui";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { plainRefusal, plural } from "@oura/newscan/copy";
import { Muted } from "./Muted";
import { WordRow } from "./WordRow";
import { openGates } from "./openDecisions";
import { focusWordField } from "./wordFocus";

const SCOPE = "newscan-words";

const WORDS = {
    intro: (k: number, total: number) => `${plural(k, "word")} write the ${total}. Change one and every question using it changes.`,
    empty: "No words yet. They appear once the 50 are drafted.",
    missingNote: (n: number) => `${n} missing`,
    redraft: "Draft the words again",
    redraftFine: "Uses two AI calls",
    changed: (n: number) => (n === 0 ? "No question changed." : `Changed in ${plural(n, "question")}.`),
    /** A typed word can make two questions alike: the new card is named here, so the count going up is explained. */
    raised: (titles: string[]) =>
        titles.length === 1 ? ` New to decide: ${lowerFirst(titles[0])}.` : ` ${titles.length} new to decide, listed above.`,
    skipped: (n: number) => (n === 0 ? "" : ` ${plural(n, "locked question")} kept ${n === 1 ? "its" : "their"} wording.`),
} as const;

/** Families in reading order, with the plain heading each group gets. */
const FAMILIES: Array<[WordFamily, string]> = [
    ["problem", "The problem"],
    ["service", "What they sell"],
    ["audience", "Who it is for"],
    ["price", "Price"],
    ["offer", "Offers and buying"],
    ["hours", "Hours"],
    ["place", "Places"],
    ["comparison", "How customers say it"],
    ["other", "Other words"],
    ["language", "Other languages"],
];

const groupId = (family: WordFamily) => `family-${family}`;

const lowerFirst = (t: string) => (/^[A-Z][a-z]/.test(t) ? `${t.charAt(0).toLowerCase()}${t.slice(1)}` : t);

function effectLine(effect: ScanDraftEffect): string {
    return `${WORDS.changed(effect.changed.length)}${WORDS.skipped(effect.skipped.length)}`;
}

export interface WordsViewProps {
    /** A word to bring into view and focus (an "open word" decision, a "Built from" link). */
    focusVariable?: string | null;
    /** Called once the word is focused (or cannot be found), so the caller can clear it. */
    onFocusDone?: () => void;
    className?: string;
}

export function WordsView(props: WordsViewProps) {
    return (
        <FoldScope value={SCOPE}>
            <WordsBody {...props} />
        </FoldScope>
    );
}

export default WordsView;

function WordsBody({ focusVariable, onFocusDone, className }: WordsViewProps) {
    const { ctl, goTo, canReach } = useScanFlow();
    const draft = ctl.draft;
    const openGroup = useFoldOpener(SCOPE);
    const reduced = useMediaQuery(REDUCED_MOTION);
    const root = useRef<HTMLDivElement>(null);

    /* ---- what the last save did, under the word it was for ---- */
    const [result, setResult] = useState<{ variable: string; text: string } | null>(null);
    /**
     * Saves on their way, oldest first, each with the cards open before it (so a
     * word that raises a new card can say so). Leaving one field for another
     * sends two saves back to back: each outcome is matched to its word by the
     * questions it changed, never to whichever word was saved last.
     */
    const pending = useRef(new Map<string, Set<string>>());
    const outcome = ctl.outcome;
    useEffect(() => {
        if (!outcome) return;
        const queue = Array.from(pending.current.entries());
        const usedBy = (v: string) => draft?.words.find((w) => w.variable === v)?.usedBy ?? [];
        const at = outcome.changed.length ? queue.findIndex(([v]) => usedBy(v).some((n) => outcome.changed.includes(n))) : 0;
        if (at < 0 || !queue.length) {
            // Another write (undo, a decision) moved on: the line would describe the wrong change.
            setResult(null);
            return;
        }
        const [variable, before] = queue[at];
        queue.slice(0, at + 1).forEach(([v]) => pending.current.delete(v));
        const raised = draft ? openGates(draft).filter((d) => !before.has(d.id)).map((d) => d.title) : [];
        setResult({ variable, text: `${effectLine(outcome)}${raised.length ? WORDS.raised(raised) : ""}` });
        // The draft arrives with its outcome.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outcome]);

    const patchWord = ctl.patchWord;
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const labelsRef = useRef(new Map<string, string>());
    labelsRef.current = new Map((draft?.words ?? []).map((w) => [w.variable, w.label]));
    const save = useCallback(
        async (variable: string, value: string) => {
            pending.current.delete(variable);
            pending.current.set(variable, new Set(draftRef.current ? openGates(draftRef.current).map((d) => d.id) : []));
            const failure = await patchWord(variable, value);
            if (failure) {
                pending.current.delete(variable);
                // Only this word's line goes: another word saved meanwhile keeps what it said.
                setResult((was) => (was?.variable === variable ? null : was));
                // The lint names words in braces: say them by their labels, and what to do.
                if (failure.kind !== "stale") return { ...failure, message: plainRefusal(failure.message, (v) => labelsRef.current.get(v)) };
            }
            return failure;
        },
        [patchWord],
    );

    /* ---- groups ---- */
    const words = useMemo(() => draft?.words ?? [], [draft]);
    const byN = useMemo(() => {
        const map = new Map<number, ScanDraftQuestion>();
        draft?.questions.forEach((q) => map.set(q.n, q));
        return map;
    }, [draft]);
    const groups = useMemo(
        () =>
            FAMILIES.map(([family, title]) => ({ family, title, words: words.filter((w) => w.family === family) })).filter(
                (g) => g.words.length > 0,
            ),
        [words],
    );

    /* ---- bring one word into view ---- */
    const [openedFrom, setOpenedFrom] = useState<string | null>(null);
    useEffect(() => {
        if (!focusVariable) return;
        const word = words.find((w) => w.variable === focusVariable);
        if (!word) {
            if (words.length) onFocusDone?.();
            return;
        }
        openGroup(groupId(word.family), { persist: false });
        setOpenedFrom(word.variable);
        // The group renders its rows a frame or two after it opens: focus the field the moment it exists.
        return focusWordField(root.current, word.variable, reduced, () => onFocusDone?.());
        // Once per request: the word list refreshing must not pull focus back.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusVariable, words.length]);

    if (!draft) return null;

    const readOnly = draft.status !== "ready";
    const total = draft.questions.length;
    const toBusiness = canReach("business") ? () => goTo("business") : undefined;
    const busy = ctl.busy !== null;

    if (words.length === 0) {
        return (
            <Surface className={className}>
                <Muted as="p">{WORDS.empty}</Muted>
            </Surface>
        );
    }

    return (
        <div ref={root} className={cn("space-y-4", className)}>
            <p className={cn(TYPE.tb, "m-0 text-gray-700")}>{WORDS.intro(words.length, total)}</p>

            <Surface flush className="px-5 py-2">
                <div>
                    {groups.map((g) => {
                        const missing = g.words.filter(isMissing).length;
                        return (
                            <GroupFold
                                key={g.family}
                                id={groupId(g.family)}
                                tone="violet"
                                lead="dot"
                                title={g.title}
                                count={g.words.length}
                                note={missing ? <span className="text-amber-700">{WORDS.missingNote(missing)}</span> : undefined}
                            >
                                <div>
                                    {g.words.map((w) => (
                                        <WordRow
                                            key={w.variable}
                                            word={w}
                                            questions={w.usedBy.map((n) => byN.get(n)).filter((q): q is ScanDraftQuestion => Boolean(q))}
                                            onSave={(value) => save(w.variable, value)}
                                            result={result?.variable === w.variable ? result.text : null}
                                            readOnly={readOnly}
                                            onBusinessStep={toBusiness}
                                            defaultOpen={openedFrom === w.variable}
                                            draftVersion={draft.version}
                                        />
                                    ))}
                                </div>
                            </GroupFold>
                        );
                    })}
                </div>
            </Surface>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
                <PillButton
                    variant="text"
                    icon={RefreshCwIcon}
                    busy={ctl.busy === "Drafting"}
                    disabled={readOnly || busy}
                    onClick={() => void ctl.rebuild({ refreshWords: true })}
                    aria-describedby="newscan-words-redraft-fine"
                >
                    {WORDS.redraft}
                </PillButton>
                <Muted id="newscan-words-redraft-fine" tone="ink4" size="tm">
                    {WORDS.redraftFine}
                </Muted>
            </div>
        </div>
    );
}

function isMissing(w: ScanDraftWord): boolean {
    return w.editable && w.usedBy.length > 0 && !w.value.trim();
}
